/**
 * Évaluation neutre et factuelle des critères SEO on-page d'une page.
 *
 * Source unique utilisée par le tableau SEO, le rapport Pages de référence et
 * tout autre affichage de critères, afin d'éviter des seuils divergents.
 * Les seuils reflètent les bornes "optimales" (un critère est OK uniquement
 * dans la plage recommandée, pas par bienveillance).
 */

export const SEO_THRESHOLDS = {
  titleMin: 50,
  titleMax: 70,
  metaMin: 120,
  metaMax: 170,
};

/** Longueur du title (utilise title_length si fourni, sinon la longueur du texte). */
export function getTitleLength(page) {
  return page?.title_length ?? (page?.title ? page.title.length : 0);
}

/** Longueur de la meta description. */
export function getMetaLength(page) {
  return page?.meta_length ?? (page?.meta_description ? page.meta_description.length : 0);
}

/** Nombre de H1 (nb_h1 si fourni, sinon 1 si un texte H1 existe). */
export function getH1Count(page) {
  return page?.nb_h1 ?? (page?.h1 ? 1 : 0);
}

/**
 * Retourne l'évaluation complète des critères SEO d'une page.
 * @returns {{
 *   titleLen:number, metaLen:number, h1Count:number,
 *   nbImages:number, nbWithoutAlt:number,
 *   titleOk:boolean, metaOk:boolean, h1Ok:boolean, imagesOk:boolean, canonOk:boolean
 * }}
 */
/** Phrase d'impact business selon les critères SEO en échec (tableau + exports). */
export function seoImpactPhrase(criteria) {
  const issues = [];
  if (!criteria.titleOk) issues.push('titre à optimiser (visibilité Google)');
  if (!criteria.metaOk) issues.push('meta description à revoir (taux de clic)');
  if (!criteria.h1Ok) issues.push('H1 manquant (sujet de page peu clair)');
  if (!criteria.imagesOk) issues.push('images sans texte alternatif (SEO images & accessibilité)');
  if (!criteria.canonOk) issues.push('canonical absente (risque de contenu dupliqué)');
  if (issues.length === 0) return 'Tous les critères SEO de base sont respectés.';
  return issues.join(' · ');
}

export function evaluateSeoCriteria(page) {
  const titleLen = getTitleLength(page);
  const metaLen = getMetaLength(page);
  const h1Count = getH1Count(page);
  const nbImages = page?.nb_images || 0;
  const nbWithoutAlt = page?.nb_images_without_alt || 0;

  return {
    titleLen,
    metaLen,
    h1Count,
    nbImages,
    nbWithoutAlt,
    titleOk: titleLen >= SEO_THRESHOLDS.titleMin && titleLen <= SEO_THRESHOLDS.titleMax,
    metaOk: metaLen >= SEO_THRESHOLDS.metaMin && metaLen <= SEO_THRESHOLDS.metaMax,
    h1Ok: h1Count >= 1,
    imagesOk: nbImages === 0 || nbWithoutAlt === 0,
    canonOk: page?.score_canonical === 100 || !!page?.canonical_url,
  };
}

/**
 * Libellé explicite + recommandation pour la longueur du title.
 * @returns {{ label: string, tone: 'ok'|'warning'|'error', recommendation: string|null }}
 */
export function getTitleLengthStatus(titleLen, hasTitle = titleLen > 0) {
  const { titleMin, titleMax } = SEO_THRESHOLDS;
  if (!hasTitle) {
    return {
      label: 'Absent',
      tone: 'error',
      recommendation:
        'Ajoutez une balise <title> unique : c’est le lien bleu affiché dans Google.',
    };
  }
  if (titleLen < titleMin) {
    return {
      label: 'Trop court',
      tone: 'warning',
      recommendation: `Visez ${titleMin}–${titleMax} caractères (${titleLen} actuellement). Enrichissez le titre avec vos mots-clés principaux.`,
    };
  }
  if (titleLen > titleMax) {
    return {
      label: 'Trop long',
      tone: 'warning',
      recommendation: `Raccourcissez à ${titleMax} caractères maximum (${titleLen} actuellement). Google tronque le surplus dans les résultats.`,
    };
  }
  return {
    label: 'Longueur optimale',
    tone: 'ok',
    recommendation: null,
  };
}

/**
 * Libellé explicite + recommandation pour la longueur de la meta description.
 */
export function getMetaLengthStatus(metaLen, hasMeta = metaLen > 0) {
  const { metaMin, metaMax } = SEO_THRESHOLDS;
  if (!hasMeta) {
    return {
      label: 'Absente',
      tone: 'error',
      recommendation:
        'Rédigez une meta description : c’est l’argumentaire affiché sous le titre dans Google.',
    };
  }
  if (metaLen < metaMin) {
    return {
      label: 'Trop courte',
      tone: 'warning',
      recommendation: `Visez ${metaMin}–${metaMax} caractères (${metaLen} actuellement). Développez un texte incitatif pour le clic.`,
    };
  }
  if (metaLen > metaMax) {
    return {
      label: 'Trop longue',
      tone: 'warning',
      recommendation: `Raccourcissez à ${metaMax} caractères maximum (${metaLen} actuellement). L’extrait sera coupé dans les résultats.`,
    };
  }
  return {
    label: 'Longueur optimale',
    tone: 'ok',
    recommendation: null,
  };
}
